import axiosInstance from "./axiosInstance";

export type IdName = { id: string | number; name: string };
export type PostalCode = { id: string | number; postalCode: string; placeName?: string };

/** One geo node in a reverse-geocode result. */
export interface GeoRef { id: number; name: string; }

/** Nearest geo hierarchy to a coordinate (GET /geodata/reverse). */
export interface ReverseGeocode {
  country?: GeoRef;
  admin1?: GeoRef;
  admin2?: GeoRef;
  city?: GeoRef;
}

function authHeader(token?: string) {
  if (!token || token.trim().length === 0) return {};
  return { Authorization: `Bearer ${token}` };
}

export async function reverseGeocode(
  token: string | undefined,
  lat: number,
  lon: number
): Promise<ReverseGeocode> {
  const res = await axiosInstance.get<ReverseGeocode>("/geodata/reverse", {
    headers: authHeader(token),
    params: { lat, lon },
  });
  return res.data;
}

// Countries pinned to the top of the (otherwise alphabetical) dropdowns.
const PINNED_COUNTRIES = ["United States"];

export async function fetchCountries(token: string): Promise<IdName[]> {
  const response = await axiosInstance.get<IdName[]>("/geodata/countries", {
    headers: authHeader(token),
  });
  const data = response.data ?? [];
  const pinned = PINNED_COUNTRIES
    .map((name) => data.find((c) => c.name === name))
    .filter((c): c is IdName => !!c);
  return [...pinned, ...data.filter((c) => !pinned.includes(c))];
}

export async function fetchAdmin1ByCountry(token: string, countryId: string): Promise<IdName[]> {
  const response = await axiosInstance.get<IdName[]>(
    `/geodata/countries/${countryId}/admin1s`,
    { headers: authHeader(token) }
  );
  return response.data ?? [];
}

export async function fetchAdmin2ByAdmin1(token: string, admin1Id: string): Promise<IdName[]> {
  const response = await axiosInstance.get<IdName[]>(
    `/geodata/admin1s/${admin1Id}/admin2s`,
    { headers: authHeader(token) }
  );
  return response.data ?? [];
}

export async function fetchCitiesByAdmin2(token: string, admin2Id: string): Promise<IdName[]> {
  const response = await axiosInstance.get<IdName[]>(
    `/geodata/admin2s/${admin2Id}/cities`,
    { headers: authHeader(token) }
  );
  return response.data ?? [];
}

export async function fetchPostalCodesByCity(
  token: string,
  cityId: string
): Promise<PostalCode[]> {
  const response = await axiosInstance.get<PostalCode[]>(
    `/geodata/cities/${cityId}/postal-codes`,
    { headers: authHeader(token) }
  );
  return response.data ?? [];
}
